import React, { useState, useEffect } from "react";
import { 
  db, 
  auth 
} from "../../firebase";
import { 
  collection, 
  addDoc,
  doc, 
  getDoc, 
  setDoc,
  updateDoc,
  increment,
  query,
  where,
  getDocs
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import "./CreateTeamPage.css";

function CreateTeamPage() {
  // Basic Team Information
  const [teamName, setTeamName] = useState("");
  const [sport, setSport] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [customAgeGroup, setCustomAgeGroup] = useState("");
  const [showCustomAgeGroup, setShowCustomAgeGroup] = useState(false);
  const [teamType, setTeamType] = useState("");
  const [season, setSeason] = useState("");
  
  // Location & Time Zone
  const [country, setCountry] = useState("United States");
  const [stateProvince, setStateProvince] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [timeZone, setTimeZone] = useState("America/New_York");
  
  // User and subscription data
  const [userSubscription, setUserSubscription] = useState(null);
  const [currentTeamCount, setCurrentTeamCount] = useState(0);
  
  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);

  // Options for dropdowns (existing code)
  const sportOptions = ["Basketball", "Soccer", "Baseball", "Football", "Volleyball", "Hockey", "Tennis", "Swimming", "Track & Field", "Golf", "Lacrosse", "Rugby", "Other"];
  const ageGroupOptions = ["U5", "U6", "U7", "U8", "U9", "U10", "U11", "U12", "U13", "U14", "U15", "U16", "U17", "U18", "U19", "U20", "U21", "Adult (18+)", "Senior (40+)", "Custom"];
  const teamTypeOptions = ["Recreational", "House League", "Competitive", "Travel", "Elite", "Select", "Academy", "Other"];
  const seasonOptions = ["Spring 2025", "Summer 2025", "Fall 2025", "Winter 2025-26", "Year-round 2025"];
  const countryOptions = ["United States", "Canada", "Mexico", "United Kingdom", "Australia", "Other"];
  const stateProvinceOptions = {/* Your existing state options */};
  const timeZoneOptions = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Anchorage", "America/Honolulu", "America/Toronto", "America/Vancouver", "Europe/London", "Europe/Berlin", "Europe/Paris", "Asia/Tokyo", "Australia/Sydney", "Pacific/Auckland"];
  
  // Fetch user data and existing teams count on component mount
  useEffect(() => {
    const fetchUserAndTeams = async () => {
      if (!user) {
        setIsLoadingUser(false);
        return;
      }
      
      try {
        setIsLoadingUser(true);
        
        // 1. Get user document to check subscription plan
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
          console.log("No user document found, creating default");
          // Create default user with basic subscription
          const defaultUserData = {
            email: user.email,
            firstName: "",
            lastName: "",
            role: "parent",
            subscription: {
              plan: "basic",
              teamsCount: 0,
              expiresAt: new Date("November 30, 2039"),
              storageUsed: 0
            }
          };
          await setDoc(userRef, defaultUserData);
          setUserSubscription({ plan: "basic", teamsCount: 0 });
        } else {
          // Use existing user data
          const userData = userDoc.data();
          if (!userData.subscription) {
            console.log("User has no subscription data, adding default");
            // Add subscription if missing
            await updateDoc(userRef, {
              subscription: {
                plan: "basic",
                teamsCount: 0,
                expiresAt: new Date("November 30, 2039"),
                storageUsed: 0
              }
            });
            setUserSubscription({ plan: "basic", teamsCount: 0 });
          } else {
            setUserSubscription(userData.subscription);
          }
        }
        
        // 2. Count teams where user is coach for accurate quota checking
        const teamsQuery = query(
          collection(db, "teams"), 
          where("coachId", "==", user.uid)
        );
        const teamsSnapshot = await getDocs(teamsQuery);
        setCurrentTeamCount(teamsSnapshot.size);
        
        console.log(`Found ${teamsSnapshot.size} teams where user is coach`);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setErrorMessage("Failed to check team quota. Please try again.");
      } finally {
        setIsLoadingUser(false);
      }
    };
    
    if (!loading) {
      fetchUserAndTeams();
    }
  }, [user, loading]);
  
  // Auto-select state/province options based on country
  useEffect(() => {
    setStateProvince("");
  }, [country]);

  // Handle age group selection
  useEffect(() => {
    if (ageGroup === "Custom") {
      setShowCustomAgeGroup(true);
    } else {
      setShowCustomAgeGroup(false);
      setCustomAgeGroup("");
    }
  }, [ageGroup]);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!user) {
      setErrorMessage("You must be logged in to create a team.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    
    // Check user's quota before creating team
    const planLimit = userSubscription?.plan === "pro" ? 5 : 1;
    
    if (currentTeamCount >= planLimit) {
      setErrorMessage(`Team quota exceeded for your ${userSubscription?.plan || "basic"} plan (${currentTeamCount}/${planLimit}). Please upgrade to create more teams.`);
      setIsSubmitting(false);
      return;
    }

    // Determine the final age group value (either selected or custom)
    const finalAgeGroup = ageGroup === "Custom" ? customAgeGroup : ageGroup;

    try {
      // Use the minimal working approach to create the team
      const teamData = {
        // Basic Team Information
        name: teamName,
        sport: sport,
        ageGroup: finalAgeGroup,
        teamType: teamType,
        season: season,
        
        // Location Information
        country: country,
        stateProvince: stateProvince,
        city: city,
        zipCode: zipCode,
        timeZone: timeZone,
        
        // Critical fields for security rules
        admins: [user.uid],
        members: [user.uid],
        coachId: user.uid,  // Set current user as coach
        
        // Other required fields
        playerCount: 0, 
        createdAt: new Date(),
        wins: 0,
        losses: 0,
        ties: 0,
        pointsScored: 0,
        pointsAllowed: 0
      };
      
      // Create the team using addDoc
      const teamRef = await addDoc(collection(db, "teams"), teamData);
      console.log("Team created with ID:", teamRef.id);
      
      // Update the user's team count
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        "subscription.teamsCount": increment(1)
      });
      
      // Update local count for UI
      setCurrentTeamCount(prevCount => prevCount + 1);
      
      alert("Team created successfully!");
      navigate("/my-teams");
    } catch (err) {
      console.error("Error creating team:", err);
      setErrorMessage(err.message || "Failed to create team. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || isLoadingUser) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="create-team-container">
      <div className="create-team-card">
        <h2>Create a New Team</h2>
        
        {/* Team quota indicator */}
        <div className="team-quota-indicator" style={{
          marginBottom: "20px",
          padding: "10px",
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
          textAlign: "center"
        }}>
          <p>Your <strong>{userSubscription?.plan || "basic"}</strong> plan allows <strong>{userSubscription?.plan === "pro" ? "5" : "1"}</strong> teams.</p>
          <p>You are currently using <strong>{currentTeamCount}</strong> out of <strong>{userSubscription?.plan === "pro" ? "5" : "1"}</strong> teams.</p>
          {currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1) && (
            <p style={{ color: "#d32f2f", fontWeight: "bold" }}>
              You have reached your team limit. Please upgrade your subscription to create more teams.
            </p>
          )}
        </div>
        
        {errorMessage && (
          <div className="error-message">
            {errorMessage}
          </div>
        )}
        
        <form onSubmit={handleCreateTeam}>
          <fieldset>
            <legend>Basic Team Information</legend>
            
            <div className="form-group">
              <label htmlFor="teamName">Team Name*</label>
              <input
                id="teamName"
                type="text"
                placeholder="E.g., Oakville Raptors"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                required
                className="form-input"
                disabled={isSubmitting || currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1)}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="sportSelect">Sport*</label>
              <select
                id="sportSelect" 
                value={sport}
                onChange={(e) => setSport(e.target.value)}
                required
                className="form-select"
                disabled={isSubmitting || currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1)}
              >
                <option value="" disabled>Select a sport</option>
                {sportOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="ageGroupSelect">Age Group</label>
              <select
                id="ageGroupSelect" 
                value={ageGroup}
                onChange={(e) => setAgeGroup(e.target.value)}
                className="form-select"
                disabled={isSubmitting || currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1)}
              >
                <option value="" disabled>Select age group</option>
                {ageGroupOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            {showCustomAgeGroup && (
              <div className="form-group">
                <label htmlFor="customAgeGroup">Custom Age Group*</label>
                <input
                  id="customAgeGroup"
                  type="text"
                  placeholder="E.g., U4, U7, U23, 30+"
                  value={customAgeGroup}
                  onChange={(e) => setCustomAgeGroup(e.target.value)}
                  required
                  className="form-input"
                  disabled={isSubmitting || currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1)}
                />
                <small className="helper-text">Enter any custom age group format (e.g., U4, U7, U23, 30+)</small>
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="teamTypeSelect">Team Type</label>
              <select
                id="teamTypeSelect" 
                value={teamType}
                onChange={(e) => setTeamType(e.target.value)}
                className="form-select"
                disabled={isSubmitting || currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1)}
              >
                <option value="" disabled>Select team type</option>
                {teamTypeOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="seasonSelect">Season/Year*</label>
              <select
                id="seasonSelect" 
                value={season}
                onChange={(e) => setSeason(e.target.value)}
                required
                className="form-select"
                disabled={isSubmitting || currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1)}
              >
                <option value="" disabled>Select season</option>
                {seasonOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </fieldset>
          
          <fieldset>
            <legend>Location & Time Zone</legend>
            
            <div className="form-group">
              <label htmlFor="countrySelect">Country*</label>
              <select
                id="countrySelect" 
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
                className="form-select"
                disabled={isSubmitting || currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1)}
              >
                {countryOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            {stateProvinceOptions[country] && (
              <div className="form-group">
                <label htmlFor="stateProvinceSelect">
                  {country === "United States" ? "State" : 
                   country === "Canada" ? "Province" :
                   country === "Australia" ? "State/Territory" :
                   country === "United Kingdom" ? "Country" : "State/Province"}
                </label>
                <select
                  id="stateProvinceSelect" 
                  value={stateProvince}
                  onChange={(e) => setStateProvince(e.target.value)}
                  className="form-select"
                  disabled={isSubmitting || currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1)}
                >
                  <option value="" disabled>
                    Select {country === "United States" ? "state" : 
                            country === "Canada" ? "province" :
                            country === "Australia" ? "state/territory" :
                            country === "United Kingdom" ? "country" : "state/province"}
                  </option>
                  {stateProvinceOptions[country]?.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="cityInput">City</label>
              <input
                id="cityInput"
                type="text"
                placeholder="E.g., Oakville"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="form-input"
                disabled={isSubmitting || currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1)}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="zipInput">Zip/Postal Code</label>
              <input
                id="zipInput"
                type="text"
                placeholder="E.g., 90210"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                className="form-input"
                disabled={isSubmitting || currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1)}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="timeZoneSelect">Time Zone</label>
              <select
                id="timeZoneSelect" 
                value={timeZone}
                onChange={(e) => setTimeZone(e.target.value)}
                className="form-select"
                disabled={isSubmitting || currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1)}
              >
                {timeZoneOptions.map((option) => (
                  <option key={option} value={option}>
                    {option.replace("_", " ").replace("/", " / ")}
                  </option>
                ))}
              </select>
            </div>
          </fieldset>
          
          <div className="form-actions">
            <button 
              type="button" 
              onClick={() => navigate("/my-teams")}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isSubmitting || currentTeamCount >= (userSubscription?.plan === "pro" ? 5 : 1)}
            >
              {isSubmitting ? "Creating..." : "Create Team"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateTeamPage;